// Multiplayer Game State Management
import { shuffleDeck, loadDeck } from '../CardData';
import { handleSpecialCardEffects, shuffleArray } from './CardRulesEngine';

// Generate unique ID for cards
const generateUniqueCardId = (card, prefix = '') => {
  return `${prefix}${card.name}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

// Player state structure
export const createPlayerState = (playerId, playerName, isAI = false, customDeck = null) => {
  // Use custom deck if provided (for AI), otherwise load player's deck
  const baseDeck = customDeck || loadDeck();
  const initialDeck = shuffleDeck(baseDeck).map(card => ({
    ...card,
    id: generateUniqueCardId(card, `${playerId}-deck-`)
  }));

  return {
    id: playerId,
    name: playerName,
    isAI,
    deck: initialDeck,
    hand: [],
    playedCards: [],
    discardPile: [],
    energy: 0,
    spaces: 10,
    usedSpaces: 0,
    isReady: false,
    isFinished: false // Track if player has finished (used all spaces or out of cards)
  };
};

// Multiplayer game state
export class MultiplayerGameState {
  constructor(players = [], gameMode = 'local') {
    this.players = players; // Array of player states
    this.currentPlayerIndex = 0;
    this.turnNumber = 0;
    this.gameMode = gameMode; // 'local', 'ai', or 'online'
    this.gameStarted = false;
    this.gameEnded = false;
    this.winner = null;
    this.centerCards = []; // Cards played from opponent's discard - shared area
  }

  // Get current player
  getCurrentPlayer() {
    return this.players[this.currentPlayerIndex];
  }

  // Get opponent(s) for current player
  getOpponents() {
    return this.players.filter((_, index) => index !== this.currentPlayerIndex);
  }

  // Get player by ID
  getPlayerById(playerId) {
    return this.players.find(p => p.id === playerId);
  }

  // Initialize game - deal initial hands
  initializeGame() {
    this.players.forEach(player => {
      // Draw initial hand (5 cards)
      for (let i = 0; i < 5; i++) {
        this.drawCardForPlayer(player.id);
      }
    });
    this.gameStarted = true;
    return this;
  }

  // Draw a card for a player
  drawCardForPlayer(playerId) {
    const player = this.getPlayerById(playerId);
    if (!player || player.deck.length === 0) {
      return null;
    }

    const drawnCard = player.deck.shift();
    player.hand.push(drawnCard);
    return drawnCard;
  }

  // Play a card from hand
  playCardFromHand(playerId, cardIndex, targetArea = 'played') {
    const player = this.getPlayerById(playerId);
    if (!player || cardIndex < 0 || cardIndex >= player.hand.length) {
      return false;
    }

    const card = player.hand.splice(cardIndex, 1)[0];
    
    if (targetArea === 'played') {
      player.playedCards.push(card);
      player.usedSpaces++;
      
      // Handle special card effects
      const effects = handleSpecialCardEffects(card.name, player);
      this.applyCardEffects(playerId, effects);
    } else if (targetArea === 'discard') {
      player.discardPile.push(card);
    }

    return card;
  }
  
  // Apply card effects to a player
  applyCardEffects(playerId, effects) {
    const player = this.getPlayerById(playerId);
    if (!player) return;
    
    // Apply space modifier
    if (effects.spaceModifier) {
      player.spaces += effects.spaceModifier;
    }
    
    // Draw additional cards
    if (effects.drawCards > 0) {
      for (let i = 0; i < effects.drawCards; i++) {
        this.drawCardForPlayer(playerId);
      }
    }
    
    // Add cards to deck
    if (effects.cardsToAddToDeck && effects.cardsToAddToDeck.length > 0) {
      effects.cardsToAddToDeck.forEach(card => {
        player.deck.push(card);
      });
      // Shuffle deck after adding cards
      player.deck = shuffleArray(player.deck);
    }
    
    // Return modal requirement if any
    return effects.requiresModal;
  }

  // Play a card from opponent's discard pile (multiplayer feature)
  // Cards go to CENTER area - count as prereqs for both players
  // But effects only apply to the player who played the card
  playCardFromOpponentDiscard(playerId, opponentId, cardIndex) {
    const player = this.getPlayerById(playerId);
    const opponent = this.getPlayerById(opponentId);

    if (!player || !opponent || cardIndex < 0 || cardIndex >= opponent.discardPile.length) {
      return null;
    }

    // Remove card from opponent's discard pile
    const card = opponent.discardPile.splice(cardIndex, 1)[0];
    
    // Add to center cards with ownership tracking
    // The card counts for prerequisites for BOTH players
    // But energy and effects only count for the player who played it
    const centerCard = {
      ...card,
      playedBy: playerId, // Track who played this card (for energy calculation)
      stolenFrom: opponentId, // Track where it came from
    };
    
    this.centerCards.push(centerCard);
    player.usedSpaces++; // Still uses a space for the player
    
    // Handle special card effects - only for the player who played the card
    const effects = handleSpecialCardEffects(card.name, player);
    this.applyCardEffects(playerId, effects);

    return card;
  }
  
  // Get center cards played by a specific player (for energy calculation)
  getCenterCardsByPlayer(playerId) {
    return this.centerCards.filter(card => card.playedBy === playerId);
  }
  
  // Get all center cards (for prerequisite checking)
  getCenterCards() {
    return this.centerCards;
  }

  // Discard a card
  discardCard(playerId, cardIndex) {
    return this.playCardFromHand(playerId, cardIndex, 'discard');
  }

  // Check if a player is finished (can no longer take actions)
  isPlayerFinished(player) {
    const hasNoCardsLeft = player.deck.length === 0 && player.hand.length === 0;
    const usedAllSpaces = player.usedSpaces >= player.spaces;
    return hasNoCardsLeft || usedAllSpaces;
  }

  // End current player's turn
  endTurn() {
    const currentPlayer = this.getCurrentPlayer();
    
    // Draw a card for the current player (if they have cards in deck)
    this.drawCardForPlayer(currentPlayer.id);
    
    // Check if current player just finished
    if (this.isPlayerFinished(currentPlayer)) {
      currentPlayer.isFinished = true;
    }

    // Move to next player
    this.currentPlayerIndex = (this.currentPlayerIndex + 1) % this.players.length;
    this.turnNumber++;

    // Skip players who are finished - find next active player
    let loopCount = 0;
    while (this.getCurrentPlayer().isFinished && loopCount < this.players.length) {
      this.currentPlayerIndex = (this.currentPlayerIndex + 1) % this.players.length;
      loopCount++;
    }

    // Check if game has ended (all players finished)
    this.checkGameEnd();
  }

  // Check if game has ended
  checkGameEnd() {
    // Update finished status for all players
    this.players.forEach(player => {
      if (this.isPlayerFinished(player)) {
        player.isFinished = true;
      }
    });

    // Game ends when ALL players have finished
    const allPlayersFinished = this.players.every(player => player.isFinished);

    if (allPlayersFinished) {
      this.gameEnded = true;
      this.players.forEach(player => {
        const ownEnergy = player.playedCards.reduce((total, card) => total + (card.energy || 0), 0);
        const centerEnergy = this.getCenterCardsByPlayer(player.id)
          .reduce((total, card) => total + (card.energy || 0), 0);
        player.energy = ownEnergy + centerEnergy;
      });
      this.determineWinner();
    }

    return this.gameEnded;
  }

  // Determine winner based on energy score
  determineWinner() {
    if (this.players.length === 0) return null;

    let maxEnergy = -Infinity;
    let winner = null;

    this.players.forEach(player => {
      if (player.energy > maxEnergy) {
        maxEnergy = player.energy;
        winner = player;
      }
    });

    this.winner = winner;
    return winner;
  }

  // Update player energy (calculated from played cards)
  updatePlayerEnergy(playerId, energy) {
    const player = this.getPlayerById(playerId);
    if (player) {
      player.energy = energy;
    }
  }

  // Update player spaces
  updatePlayerSpaces(playerId, spaces) {
    const player = this.getPlayerById(playerId);
    if (player) {
      player.spaces = spaces;
    }
  }

  // Add card to player's deck
  addCardToDeck(playerId, card) {
    const player = this.getPlayerById(playerId);
    if (player) {
      const newCard = {
        ...card,
        id: generateUniqueCardId(card, `${playerId}-added-`)
      };
      player.deck.push(newCard);
    }
  }

  // Serialize game state for network transmission
  serialize() {
    return {
      players: this.players,
      currentPlayerIndex: this.currentPlayerIndex,
      turnNumber: this.turnNumber,
      gameMode: this.gameMode,
      gameStarted: this.gameStarted,
      gameEnded: this.gameEnded,
      winner: this.winner,
      centerCards: this.centerCards
    };
  }

  // Deserialize game state
  static deserialize(data) {
    const game = new MultiplayerGameState(data.players, data.gameMode);
    game.currentPlayerIndex = data.currentPlayerIndex;
    game.turnNumber = data.turnNumber;
    game.gameStarted = data.gameStarted;
    game.gameEnded = data.gameEnded;
    game.winner = data.winner;
    game.centerCards = data.centerCards || [];
    return game;
  }

  // Clone the game state
  clone() {
    const cloned = new MultiplayerGameState(
      JSON.parse(JSON.stringify(this.players)),
      this.gameMode
    );
    cloned.currentPlayerIndex = this.currentPlayerIndex;
    cloned.turnNumber = this.turnNumber;
    cloned.gameStarted = this.gameStarted;
    cloned.gameEnded = this.gameEnded;
    cloned.winner = this.winner;
    cloned.centerCards = JSON.parse(JSON.stringify(this.centerCards));
    return cloned;
  }
}

export default MultiplayerGameState;

